import { useEffect, useState } from "react";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./DealerDetails.module.css";
import TargetIcon from "../assets/target-icon.png";
import apiService from "../../services/apiService";
import { useUser } from "../../contexts/UserContext";

const businessTypeFields = {
  proprietorship: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    {
      name: "aadhaar_number",
      label: "Aadhaar Number",
      type: "text",
      required: true,
    },
    { name: "pan_number", label: "PAN Number", type: "text", required: true },
  ],
  partnership: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
  ],
  limited_liability_partnership: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    {
      name: "company_pan_number",
      label: "Company PAN Number",
      type: "text",
      required: true,
    },
  ],
  one_person_company: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    {
      name: "company_pan_number",
      label: "Company PAN Number",
      type: "text",
      required: true,
    },
    {
      name: "gst_number",
      label: "GSTIN Number",
      type: "text",
      required: true,
    },
  ],
  private_limited_company: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    {
      name: "company_pan_number",
      label: "Company PAN Number",
      type: "text",
      required: true,
    },
    {
      name: "gst_number",
      label: "GSTIN Number",
      type: "text",
      required: true,
    },
  ],
  individual: [
    {
      name: "business_name",
      label: "Business Name",
      type: "text",
      required: true,
    },
    { name: "name", label: "Name", type: "text", required: true },
    {
      name: "aadhaar_number",
      label: "Aadhaar Number",
      type: "text",
      required: true,
    },
    { name: "pan_number", label: "PAN Number", type: "text", required: true },
  ],
};

const businessTypes = [
  { label: "Individual", value: "individual" },
  { label: "Proprietorship", value: "proprietorship" },
  { label: "Partnership", value: "partnership" },
  {
    label: "Limited Liability Partnership (LLP)",
    value: "limited_liability_partnership",
  },
  { label: "One Person Company", value: "one_person_company" },
  { label: "Private Limited Company", value: "private_limited_company" },
];

export default function DealerDetails() {
  const navigate = useNavigate();
  const { currentUser, loading } = useUser();

  const [checkingNavigate, setCheckingNavigate] = useState(true);
  const [businessType, setBusinessType] = useState("individual");
  const [form, setForm] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [partners, setPartners] = useState([""]);
  const [isValidationPending, setIsValidationPending] = useState(false);
  const [isNotValid, setIsNotValid] = useState(false);
  const [notValidComments, setNotValidComments] = useState("");
  const [showNotValidPopup, setShowNotValidPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validationStatesRef = useRef(null);

  const redirectionStates = ["pending", "not_valid"];

  const navigateToPendingStage = async (validation_states) => {
    await new Promise((r) => setTimeout(r, 500));
    if (redirectionStates.includes(validation_states.details.status)) {
      setCheckingNavigate(false);
      return;
    } else if (validation_states.payment.status === "pending") {
      return navigate("/dealer-onboarding/subscription");
    } else if (validation_states.documents.status === "pending") {
      return navigate("/dealer-onboarding/upload-docs");
    } else if (
      validation_states.details.status === "validated" ||
      validation_states.contract.status === "pending"
    ) {
      return navigate("/dealer-onboarding/contract");
    }
  };

  useEffect(() => {
    if (loading) return;

    // fetch existing dealer details to prefill the form
    const fetchDealerDetails = async () => {
      try {
        const response = await apiService.dealerOnboardingDetails({
          action: "get",
        });

        if (!response.data) {
          console.error("No data received from dealerOnboardingDetails API");
          return;
        }

        if (response.data.error) {
          console.error(
            "Error fetching dealer details:",
            response.data.message
          );
          return;
        }

        const validation_states = response.data.stage_validation_states;
        validationStatesRef.current = validation_states;
        await navigateToPendingStage(validation_states);

        if (response.data.details?.company_type) {
          setBusinessType(response.data.details.company_type);
        }
        setForm(response.data.details ?? {});

        if (response.data.details?.partners) {
          setPartners(response.data.details.partners);
        }

        setIsValidationPending(
          validation_states.details.status === "validation_pending"
        );

        setIsNotValid(validation_states.details.status === "not_valid");

        if (
          validation_states.details.status === "not_valid" &&
          validation_states.details.comments
        ) {
          setNotValidComments(validation_states.details.comments);
          setShowNotValidPopup(true);
        }

        setCheckingNavigate(false);
      } catch (error) {
        console.error("Error fetching dealer details:", error);
      }
    };

    fetchDealerDetails();
  }, [navigate, currentUser, loading]);

  const handleChange = (e) => {
    if (isValidationPending) return;
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePartnerChange = (index, value) => {
    if (isValidationPending) return;
    const updated = [...partners];
    updated[index] = value;
    setPartners(updated);
  };

  const addPartner = () => {
    if (isValidationPending) return;
    setPartners((prev) => [...prev, ""]);
  };

  const handleFormError = (response) => {
    if (response.data.error) {
      const mappedErrors = {};
      if (Array.isArray(response.data.message)) {
        response.data.message.forEach((err) => {
          mappedErrors[err.field] = err.message;
        });
      } else {
        alert("something went wrong");
      }

      setFieldErrors(mappedErrors);
      return;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isValidationPending || isSubmitting) return;
    setIsSubmitting(true);
    const data = { ...form, company_type: businessType };
    if (
      businessType === "partnership" ||
      businessType === "limited_liability_partnership"
    ) {
      data.partners = partners.filter((p) => p.trim() !== "");
    }

    await new Promise((r) => setTimeout(r, 500));

    try {
      const response = await apiService.dealerOnboardingDetails({
        details: data,
      });
      if (response.data.error) {
        handleFormError(response);
      } else {
        alert("details submitted successfully!");
        window.dispatchEvent(new Event("userDataUpdated"));
        navigate("/dealer-onboarding/subscription");
      }
    } catch (error) {
      console.error("Error submitting dealer details", error);
      handleFormError(error.response);
    } finally {
      setIsSubmitting(false);
    }
  };

  return checkingNavigate ? (
    <div className={`container ${styles.container}`}>
      <div className={styles.checkNavigate}>Please wait...</div>
    </div>
  ) : (
    <>
      <div className={`container ${styles.container}`}>
        <h1 className={styles.title}>Let's set things up</h1>
        {/* Not Valid Comments Link/Button and Popup */}
        {isNotValid && notValidComments && (
          <div style={{ textAlign: "center", margin: "1em 0" }}>
            <button
              type="button"
              style={{
                background: "none",
                color: "#d32f2f",
                border: "none",
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "1em",
                padding: 0,
              }}
              onClick={() => setShowNotValidPopup(true)}
            >
              Please review the changes needed
            </button>
          </div>
        )}
        {showNotValidPopup && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.4)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "2em 2.5em",
                borderRadius: "8px",
                maxWidth: "90vw",
                maxHeight: "80vh",
                overflowY: "auto",
                boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
                textAlign: "center",
              }}
            >
              <h2 style={{ color: "#d32f2f", marginBottom: "1em" }}>
                Details Not Valid
              </h2>
              <div
                style={{
                  marginBottom: "1.5em",
                  color: "#333",
                  whiteSpace: "pre-wrap",
                  textAlign: "left",
                }}
              >
                {notValidComments}
              </div>
              <button
                style={{
                  padding: "0.5em 1.5em",
                  background: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "1em",
                }}
                onClick={() => setShowNotValidPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
        <div className={styles.formWrapper}>
          <form className={styles.form} onSubmit={handleSubmit}>
            {/* LEFT COLUMN - Business Type */}
            <div className={`${styles.column} ${styles.businessTypeColumn}`}>
              <h2>You are a</h2>
              {businessTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={
                    type.value === businessType
                      ? styles.selectedBusinessType
                      : ""
                  }
                  onClick={() => {
                    if (isValidationPending || isNotValid) return;
                    setBusinessType(type.value);
                    setForm({});
                    setPartners([""]);
                  }}
                  disabled={isValidationPending || isNotValid}
                >
                  {businessType === type.value && (
                    <img src={TargetIcon} alt="selected" />
                  )}
                  {type.label}
                </button>
              ))}
            </div>

            {/* MIDDLE COLUMN - Form Fields */}
            <div className={styles.column}>
              {businessTypeFields[businessType].map((field) => (
                <div key={field.name} className={styles.inputGroup}>
                  <label className={styles.label} htmlFor={field.name}>
                    {field.label}
                  </label>
                  <input
                    id={field.name}
                    name={field.name}
                    type={field.type}
                    value={form[field.name] || ""}
                    onChange={handleChange}
                    placeholder={field.label}
                    required={field.required}
                    disabled={isValidationPending}
                    className={fieldErrors[field.name] ? styles.errorInput : ""}
                  />
                  {fieldErrors[field.name] && (
                    <p className={styles.errorText}>
                      {fieldErrors[field.name]}
                    </p>
                  )}
                </div>
              ))}

              {/* Dynamic Partners Section */}
              {(businessType === "partnership" ||
                businessType === "limited_liability_partnership") && (
                <>
                  <div className={styles.partnersSection}>
                    {partners.map((partner, index) => (
                      <div key={index} className={styles.inputGroup}>
                        <label className={styles.label}>
                          Partner {index + 1}
                        </label>
                        <input
                          type="text"
                          placeholder="Enter Partner Name"
                          value={partner}
                          onChange={(e) =>
                            handlePartnerChange(index, e.target.value)
                          }
                          className={styles.partnerInput}
                          required
                          disabled={isValidationPending}
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={styles.addPartnerBtn}
                    onClick={addPartner}
                    disabled={isValidationPending}
                  >
                    Add Partner
                  </button>
                </>
              )}
            </div>

            {/* RIGHT COLUMN - Mailing Address */}
            <div className={styles.column}>
              <label className={styles.label} htmlFor="mailing-address">
                Mailing Address
              </label>
              <textarea
                name="mailing_address"
                placeholder="Enter your mailing address"
                required
                onChange={handleChange}
                value={form.mailing_address || ""}
                disabled={isValidationPending}
              />
              <button
                type="submit"
                className={styles.PaymentBtn}
                disabled={isValidationPending || isSubmitting}
              >
                {isSubmitting
                  ? "Please wait..."
                  : isNotValid
                  ? "Resubmit for Validation"
                  : "Proceed to Payment"}
              </button>
              {isValidationPending && (
                <div
                  className={styles.validationPendingMsg}
                  style={{ color: "red", marginTop: "1em" }}
                >
                  Your details are currently under validation. You cannot edit
                  the form until validation is complete.
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
